import { useEffect, useRef, useState } from 'react';
import { context } from '@shared-services/context-service';
import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';

const REFRESH_INTERVAL_MS = 2000;
const MAX_REFRESH_ATTEMPTS = 15;

export type OrderState =
    | { status: 'loading' }
    | {
          status: 'success';
          order: OrderResponseDto;
          isRefreshing: boolean;
          isWaitingForBookingConfirmation: boolean;
      }
    | { status: 'empty' }
    | { status: 'error' };

export function readOrderIdFromUrl(): string | null {
    if (context.isServer || typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('order_id');
}

export function hasUnconfirmedBookings(order: OrderResponseDto): boolean {
    return order.items.some(
        (item) => item.booking !== null && item.booking.id === null
    );
}

export function hasAsyncPayment(order: OrderResponseDto): boolean {
    if (!order.payment) return true;
    if (order.payment.confirmed_method.name === 'UNKNOWN') return true;
    return false;
}

export function shouldAutoRefresh(order: OrderResponseDto): boolean {
    const unconfirmedBookings = hasUnconfirmedBookings(order);
    const asyncPayment = hasAsyncPayment(order);

    if (order.invoice_number && !asyncPayment && !unconfirmedBookings) {
        return false;
    }

    return unconfirmedBookings || asyncPayment;
}

export function useOrder(): OrderState {
    const [state, setState] = useState<OrderState>(() => {
        if (context.isServer || typeof window === 'undefined') {
            return { status: 'loading' };
        }
        return readOrderIdFromUrl() ? { status: 'loading' } : { status: 'empty' };
    });

    const attemptsRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (context.isServer || typeof window === 'undefined') {
            return;
        }

        const order_id = readOrderIdFromUrl();
        if (!order_id) {
            setState({ status: 'empty' });
            return;
        }

        cancelledRef.current = false;
        attemptsRef.current = 0;

        const scheduleRefreshIfNeeded = (order: OrderResponseDto) => {
            if (cancelledRef.current) return;
            if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS) return;
            if (!shouldAutoRefresh(order)) return;

            timerRef.current = setTimeout(() => {
                void refreshOrder();
            }, REFRESH_INTERVAL_MS);
        };

        const refreshOrder = async () => {
            if (cancelledRef.current) return;
            if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS) {
                setState((prev) =>
                    prev.status === 'success'
                        ? {
                              ...prev,
                              isRefreshing: false,
                              isWaitingForBookingConfirmation: false,
                          }
                        : prev
                );
                return;
            }

            attemptsRef.current += 1;
            setState((prev) =>
                prev.status === 'success'
                    ? { ...prev, isRefreshing: true }
                    : prev
            );

            try {
                const refreshed = await window._ecomm!.getOrder({ order_id });
                if (cancelledRef.current) return;

                setState({
                    status: 'success',
                    order: refreshed,
                    isRefreshing: false,
                    isWaitingForBookingConfirmation:
                        hasUnconfirmedBookings(refreshed) &&
                        shouldAutoRefresh(refreshed) &&
                        attemptsRef.current < MAX_REFRESH_ATTEMPTS,
                });

                scheduleRefreshIfNeeded(refreshed);
            } catch {
                if (cancelledRef.current) return;
                setState((prev) =>
                    prev.status === 'success'
                        ? { ...prev, isRefreshing: false }
                        : prev
                );
                // Vue keeps trying on errors as long as we're under the cap.
                scheduleRefreshAfterError();
            }
        };

        const scheduleRefreshAfterError = () => {
            if (cancelledRef.current) return;
            if (attemptsRef.current >= MAX_REFRESH_ATTEMPTS) return;
            timerRef.current = setTimeout(() => {
                void refreshOrder();
            }, REFRESH_INTERVAL_MS);
        };

        const fetchInitial = async () => {
            try {
                if (!window._ecomm) {
                    setState({ status: 'error' });
                    return;
                }
                await window._ecomm.ready;
                if (cancelledRef.current) return;
                const order = await window._ecomm.getOrder({ order_id });
                if (cancelledRef.current) return;
                setState({
                    status: 'success',
                    order,
                    isRefreshing: false,
                    isWaitingForBookingConfirmation:
                        hasUnconfirmedBookings(order) &&
                        shouldAutoRefresh(order),
                });
                scheduleRefreshIfNeeded(order);
            } catch {
                if (!cancelledRef.current) {
                    setState({ status: 'error' });
                }
            }
        };

        void fetchInitial();

        return () => {
            cancelledRef.current = true;
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    return state;
}
